'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge } from '@common/components';
import { cn } from '@core/utils';
import { Suspense } from 'react';
import { Image } from '@/components/client';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useAuthStore } from '@/stores/AuthStore';
import { useCountdown } from '../../_hooks/useCountdown';
import { useCoupangEarn } from '../_hooks/useCoupangEarn';
import { useCoupangRecommend } from '../_hooks/useCoupangRecommend';
import { CoupangRecommendSection, CoupangRecommendSkeleton } from './CoupangRecommendSection';
const HERO_IMAGE = {
  alt: '쿠팡',
  src: '/assets/pngs/benefit-coupang/coupang-badge.png',
  width: 240,
  height: 240
} as const;

//* 가용 여부는 서버 권위(nextAvailableAt)로만 판정한다 — 배지·이미지는 틱이 불필요하고
//* 클라 시각 비교는 하이드레이션 리스크만 더한다. 초 단위 카운트다운은 버튼이 담당.
const CoupangRecommendBadge = () => {
  const $ = _c(2);
  const {
    data
  } = useCoupangEarn();
  const nickName = useAuthStore(_temp);
  if (!nickName || data.nextAvailableAtMs !== null) {
    return null;
  }
  let t0;
  if ($[0] !== nickName) {
    t0 = <Badge className="bg-O-25 text-O-600">{nickName}님 맞춤추천</Badge>;
    $[0] = nickName;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};

//* 광고ID가 앱 브릿지로만 와서 추천 유무가 곧 앱 여부다 — 웹·미확보면 로고로 되돌아간다.
//* 하루 적립 횟수는 서버 권위값이라 모르는 동안(게스트·로딩)에는 숫자를 지운 문장으로 그린다.
const RewardHeading = t0 => {
  const $ = _c(3);
  const {
    dailyRewardLimit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <br />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = dailyRewardLimit !== null ? `${dailyRewardLimit}번 ` : "";
  let t3;
  if ($[1] !== t2) {
    t3 = <h1 className="text-center font-700 text-Gray-900 typo-h5">매일 버튼 누르고{t1}{t2}웰포인트 받아요</h1>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
};
const CoupangRewardHeading = () => {
  const $ = _c(2);
  const {
    data
  } = useCoupangEarn();
  let t0;
  if ($[0] !== data.dailyRewardLimit) {
    t0 = <RewardHeading dailyRewardLimit={data.dailyRewardLimit} />;
    $[0] = data.dailyRewardLimit;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const CoupangHero = () => {
  const $ = _c(9);
  const {
    data
  } = useCoupangEarn();
  const {
    isAvailable
  } = useCountdown(data.nextAvailableAtMs);
  const {
    products,
    isLoading,
    isAdvertisingIdPending
  } = useCoupangRecommend();
  if (isAdvertisingIdPending || isLoading) {
    let t0;
    if ($[0] !== isAvailable) {
      t0 = <CoupangRecommendSkeleton isAvailable={isAvailable} />;
      $[0] = isAvailable;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (products.length > 0) {
    let t0;
    if ($[2] !== isAvailable || $[3] !== products) {
      t0 = <CoupangRecommendSection products={products} isAvailable={isAvailable} />;
      $[2] = isAvailable;
      $[3] = products;
      $[4] = t0;
    } else {
      t0 = $[4];
    }
    return t0;
  }
  const t0 = data.nextAvailableAtMs !== null && "opacity-40";
  let t1;
  if ($[5] !== t0) {
    t1 = cn(t0);
    $[5] = t0;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <Image {...HERO_IMAGE} className={t1} />;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};

//* 웹에서는 적립 자체가 불가능하다 — 진입하자마자 모달로 가로막는 대신 여기서 먼저 알린다.
//* 앱 판별 전(isChecking)에는 아무것도 그리지 않아 배지가 깜빡이지 않는다.
const StatusBadge = t0 => {
  const $ = _c(2);
  const {
    isGuest: serverIsGuest
  } = t0;
  const isGuest = useIsGuest(serverIsGuest);
  const {
    isChecking,
    isWelloApp
  } = useCheckWelloApp();
  if (isChecking) {
    return null;
  }
  if (!isWelloApp) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Badge className="bg-O-25 text-O-600">모바일 앱 전용 혜택</Badge>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (isGuest) {
    return null;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Suspense fallback={null}><CoupangRecommendBadge /></Suspense>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface CoupangEarnStatusProps {
  isGuest: boolean;
}
export const CoupangEarnStatus = t0 => {
  const $ = _c(2);
  const {
    isGuest
  } = t0;
  let t1;
  if ($[0] !== isGuest) {
    t1 = <div className="flex flex-1 flex-col items-center justify-center gap-32 px-20"><div className="flex w-full flex-col items-center gap-12"><div className="flex h-24 items-center"><StatusBadge isGuest={isGuest} /></div>{isGuest ? <RewardHeading dailyRewardLimit={null} /> : <Suspense fallback={<RewardHeading dailyRewardLimit={null} />}><CoupangRewardHeading /></Suspense>}</div>{isGuest ? <Image {...HERO_IMAGE} /> : <Suspense fallback={<Image {...HERO_IMAGE} className="opacity-40" />}><CoupangHero /></Suspense>}</div>;
    $[0] = isGuest;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
function _temp(state) {
  return state.myInfo?.nickName;
}